import {
  useClaimOwnerConfig,
  useFeatureToggle,
  useUpsertClaimOwnerConfig,
} from '@dispatch/shared/hooks';
import { ClaimOwnerConfig } from '@dispatch/shared/services';
import { useCurrentUser } from '@dispatch/web/auth';
import { useNotification } from '@dispatch/web/hooks';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

const DEFAULT_TTL_MINUTES = 30;
const MIN_TTL_MINUTES = 10;
const MAX_TTL_MINUTES = 45;
const SECONDS_PER_MINUTE = 60;

const FNOL_TTL_FEATURE_ID = 'fnol-ttl-card';

interface FnolTtlFormValues {
  ttlMinutes: number;
}

const toMinutes = (config?: ClaimOwnerConfig): number => {
  const seconds = config?.fnolCacheTtlSeconds;
  if (typeof seconds === 'number' && Number.isFinite(seconds)) {
    // The config stores the TTL in seconds; round so a value that is not an
    // exact multiple of 60 still presents as a whole, editable minute count
    // rather than tripping the integer guard in validateTtl.
    return Math.round(seconds / SECONDS_PER_MINUTE);
  }
  return DEFAULT_TTL_MINUTES;
};

// Guards the value shape (a whole number is present). The 10-45 minute range
// -- which also excludes zero and negatives -- is enforced separately by the
// rules.min / rules.max declared on the Controller below; keep both in sync if
// the bounds change.
const validateTtl = (value: number): true | string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return 'Enter a whole number of minutes';
  }
  if (!Number.isInteger(value)) {
    return 'Enter a whole number of minutes';
  }
  return true;
};

/**
 * FnolRequestTtlAdmin - admin card for the SageSure FNOL request cache TTL.
 * Hidden in full unless the tenant has the fnol-ttl-card feature toggle
 * enabled. Reads the claimowner configuration, displays the TTL in minutes,
 * and writes the full configuration object back so unrelated keys are
 * preserved on save.
 */
export const FnolRequestTtlAdmin = () => {
  const { tenantId } = useCurrentUser();
  const { isEnabled } = useFeatureToggle(FNOL_TTL_FEATURE_ID, tenantId);
  const { notifySuccess, notifyError } = useNotification();

  const {
    data: config,
    isLoading,
    isError,
  } = useClaimOwnerConfig(isEnabled ? tenantId : undefined);

  // The Configuration PUT is a full replacement, so editing/saving is only safe
  // once the existing config has loaded. If the GET failed or has not populated,
  // render an error state and block Save rather than write a stripped-down doc.
  const configUnavailable = isError || config === undefined;

  const { control, formState, handleSubmit, reset } =
    useForm<FnolTtlFormValues>({
      mode: 'onChange',
      defaultValues: { ttlMinutes: DEFAULT_TTL_MINUTES },
    });

  const persistedMinutes = useMemo(() => toMinutes(config), [config]);

  useEffect(() => {
    reset({ ttlMinutes: persistedMinutes });
  }, [persistedMinutes, reset]);

  const onSaved = (savedValue: ClaimOwnerConfig) => {
    notifySuccess('Success', 'FNOL request TTL updated');
    reset({ ttlMinutes: toMinutes(savedValue) });
  };

  const onError = () => {
    notifyError('Error', 'Failed to update FNOL request TTL');
  };

  const { upsertClaimOwnerConfig, status } = useUpsertClaimOwnerConfig(
    tenantId,
    onSaved
  );

  if (!isEnabled) {
    return null;
  }

  const handleSave = (values: FnolTtlFormValues) => {
    // Never write on top of an unloaded config: the PUT is a full replacement, so
    // saving with config undefined would drop every other claimowner key.
    if (!config) {
      return;
    }

    const updatedConfig: ClaimOwnerConfig = {
      ...config,
      fnolCacheTtlSeconds: values.ttlMinutes * SECONDS_PER_MINUTE,
    };

    upsertClaimOwnerConfig(updatedConfig, { onError });
  };

  const onSaveClick = () => {
    handleSubmit(handleSave)();
  };

  return (
    <Card>
      <CardHeader
        title="FNOL Request TTL"
        sx={{
          '.MuiCardHeader-title': {
            fontSize: '18px',
          },
        }}
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress />
        ) : configUnavailable ? (
          <Typography color="error" data-testid="fnol-ttl-load-error">
            Unable to load the FNOL request configuration. Refresh and try
            again.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid size={4}>
              <Controller
                name="ttlMinutes"
                control={control}
                rules={{
                  required: 'Enter a whole number of minutes',
                  min: {
                    value: MIN_TTL_MINUTES,
                    message: `Minimum is ${MIN_TTL_MINUTES} minutes`,
                  },
                  max: {
                    value: MAX_TTL_MINUTES,
                    message: `Maximum is ${MAX_TTL_MINUTES} minutes`,
                  },
                  validate: validateTtl,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={Number.isNaN(field.value) ? '' : field.value}
                    id="fnol-ttl-minutes"
                    label="TTL (minutes)"
                    type="number"
                    variant="standard"
                    sx={{ width: '100%' }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={(event) => {
                      const raw = event.target.value;
                      field.onChange(raw === '' ? NaN : Number(raw));
                    }}
                    inputProps={{
                      'data-testid': 'fnol-ttl-minutes-input',
                      step: 1,
                      min: MIN_TTL_MINUTES,
                      max: MAX_TTL_MINUTES,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
      <CardActions
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onSaveClick}
          variant="contained"
          data-testid="fnol-ttl-save-button"
          disabled={
            configUnavailable ||
            !formState.isDirty ||
            Object.keys(formState.errors).length > 0 ||
            status === 'loading'
          }
        >
          Save
        </Button>
      </CardActions>
    </Card>
  );
};
